import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { getBlogArticles, blogCategories } from '../../../mocks/blog';

export default function BlogCatalog() {
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.05, distance: 36, duration: 900 });
  const [activeCategory, setActiveCategory] = useState('Todos');

  const articles = getBlogArticles();
  const filtered = useMemo(() => {
    if (activeCategory === 'Todos') return articles;
    return articles.filter((a) => a.category === activeCategory);
  }, [activeCategory, articles]);

  return (
    <section ref={ref} id="articulos" className="w-full bg-brand-cream py-20 md:py-28">
      <div className="px-6 lg:px-[7%]">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto" style={style}>
          <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.2em] uppercase font-medium">
            Todos los artículos
          </span>
          <h2 className="font-serif text-brand-black text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.15] mt-4">
            Archivo de
            <span className="italic text-brand-gray-300"> Tendencias</span>
          </h2>
        </div>

        {/* Category tabs */}
        <div
          className="mt-10 flex flex-wrap justify-center gap-2"
          style={isVisible ? {
            opacity: 1,
            transform: 'translateY(0px)',
            transition: 'opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 150ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 150ms',
          } : {
            opacity: 0,
            transform: 'translateY(20px)',
            transition: 'opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 150ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 150ms',
            willChange: 'opacity, transform',
          }}
        >
          {blogCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`font-sans text-[13px] tracking-wider px-5 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-brand-black text-white'
                  : 'bg-white text-brand-gray-500 hover:text-brand-black hover:bg-white/80'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Articles grid */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filtered.map((article, index) => (
            <article
              key={article.id}
              className="bg-white rounded-3xl overflow-hidden group border border-transparent hover:border-brand-gray-100 transition-all duration-500 hover:-translate-y-1"
              style={isVisible ? {
                opacity: 1,
                transform: 'translateY(0px)',
                transition: `opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 100}ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 100}ms`,
              } : {
                opacity: 0,
                transform: 'translateY(36px)',
                transition: `opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 100}ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 100}ms`,
                willChange: 'opacity, transform',
              }}
            >
              <Link to={`/blog/${article.slug}`} className="block">
                <div className="relative h-[220px] md:h-[260px] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 backdrop-blur-sm text-brand-black font-sans text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full transition-all duration-300 group-hover:bg-brand-black group-hover:text-white">
                      {article.category}
                    </span>
                  </div>
                </div>
              </Link>

              <div className="p-6 md:p-8">
                <div className="flex items-center gap-3 text-brand-gray-400 text-sm">
                  <span>{article.date}</span>
                  <span className="w-1 h-1 rounded-full bg-brand-gray-300" />
                  <span>{article.readTime}</span>
                </div>

                <Link to={`/blog/${article.slug}`}>
                  <h3 className="font-serif text-brand-black text-lg font-semibold leading-[1.35] mt-4 group-hover:text-brand-gray-500 transition-colors duration-300">
                    {article.title}
                  </h3>
                </Link>

                <p className="font-sans text-brand-gray-500 text-[15px] leading-[1.7] mt-3 line-clamp-3">
                  {article.excerpt}
                </p>

                <div className="mt-5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-brand-gray-100 flex items-center justify-center">
                      <span className="font-serif text-brand-black text-xs font-medium">
                        A
                      </span>
                    </div>
                    <span className="font-sans text-brand-gray-500 text-sm">
                      {article.author}
                    </span>
                  </div>

                  <Link
                    to={`/blog/${article.slug}`}
                    className="inline-flex items-center gap-1 font-sans text-brand-black text-xs tracking-wider uppercase group-hover:text-brand-gray-500 transition-all duration-300 whitespace-nowrap group-hover:gap-2"
                  >
                    Leer
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* No results */}
        {filtered.length === 0 && (
          <div className="text-center py-16">
            <p className="font-sans text-brand-gray-400 text-lg">
              No hay artículos en esta categoría todavía.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}